import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, Image, FileText, Clock, CheckCircle, Send, Leaf, Megaphone, CalendarRange, Sparkles, Loader2, Lightbulb, TrendingUp, Copy } from "lucide-react";
import { SystemStatusCard } from "@/components/SystemStatusCard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusMap = {
  draft: { label: "Entwurf", cls: "status-draft" },
  review: { label: "Zur Pruefung", cls: "status-review" },
  approved: { label: "Freigegeben", cls: "status-approved" },
  scheduled: { label: "Geplant", cls: "status-scheduled" },
  published: { label: "Veroeffentlicht", cls: "status-published" },
  failed: { label: "Fehlgeschlagen", cls: "status-failed" },
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const [suggestCompany, setSuggestCompany] = useState("");
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [trending, setTrending] = useState([]);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    const load = async () => {
      try {
        const [s, c] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
          axios.get(`${API}/companies`, { withCredentials: true }),
        ]);
        setStats(s.data);
        setCompanies(c.data);
      } catch (e) {
        console.error(e);
      }
    };
    load();
    axios.get(`${API}/marketplace/trending?days=7&limit=3`, { withCredentials: true })
      .then((r) => setTrending(r.data.items || []))
      .catch(() => {});
  }, []);

  const generateSuggestions = async () => {
    if (!suggestCompany) return;
    setLoadingSuggestions(true);
    try {
      const r = await axios.post(
        `${API}/topics/suggest`,
        { company_id: suggestCompany },
        { withCredentials: true }
      );
      setSuggestions(r.data.suggestions || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  if (!stats) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-24 bg-white rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  const cards = [
    { label: t("dashboard.companies"), value: stats.companies, icon: Building2, accent: "text-[#2563EB]" },
    { label: t("dashboard.media"), value: stats.media, icon: Image, accent: "text-blue-600" },
    { label: t("dashboard.content"), value: stats.total_content, icon: FileText, accent: "text-emerald-600" },
    { label: t("dashboard.in_review"), value: stats.in_review, icon: Clock, accent: "text-amber-600" },
    { label: t("dashboard.scheduled"), value: stats.scheduled, icon: Send, accent: "text-indigo-600" },
    { label: t("dashboard.published"), value: stats.published, icon: CheckCircle, accent: "text-green-600" },
    { label: t("dashboard.campaigns"), value: stats.campaigns || 0, icon: Megaphone, accent: "text-purple-600" },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-8 max-w-7xl">
      <div>
        <h1 className="text-4xl font-bold font-heading tracking-tight">{t("dashboard.title")}</h1>
        <p className="text-[var(--studio-text-muted)] mt-1">{t("dashboard.subtitle")}</p>
      </div>

      {/* Season Banner */}
      {stats.season && (
        <div
          className="bg-[#0F0F0F] text-white rounded-xl p-6 flex items-center gap-4"
          data-testid="season-banner"
        >
          <Leaf className="w-8 h-8 text-[#2563EB] flex-shrink-0" />
          <div>
            <p className="font-semibold font-heading">
              {stats.season.season} &mdash; {stats.season.month_name}
            </p>
            <p className="text-sm text-white/60 mt-0.5">
              {t("dashboard.season_banner")}: {stats.season.themes?.join(", ")}
            </p>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map(({ label, value, icon: Icon, accent }) => (
          <Card
            key={label}
            className="border-[var(--studio-border)] shadow-none hover:shadow-sm transition-shadow"
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[var(--studio-text-muted)]">{label}</p>
                  <p className="text-3xl font-bold font-heading mt-1">{value}</p>
                </div>
                <Icon className={`w-8 h-8 ${accent} opacity-80`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* System Status */}
      <SystemStatusCard />

      {/* Trending Templates Widget */}
      {trending.length > 0 && (
        <Card className="border-[var(--studio-border)] shadow-none" data-testid="trending-widget">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#2563EB]" /> Trending Templates
                <Badge variant="outline" className="text-[10px]">7 Tage</Badge>
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/marketplace")}
                data-testid="trending-view-all-btn"
                className="text-xs"
              >
                Alle ansehen &rarr;
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {trending.map((tpl, idx) => (
                <button
                  key={tpl.id}
                  onClick={() => navigate("/marketplace")}
                  data-testid={`trending-item-${tpl.id}`}
                  className="text-left p-4 rounded-lg border border-[var(--studio-border)] hover:border-[#2563EB] transition-colors group"
                >
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-lg font-bold font-heading text-[var(--studio-text-muted)]">#{idx + 1}</span>
                    <div className="flex items-center gap-1 text-xs text-[var(--studio-text-muted)]">
                      <Copy className="w-3 h-3" /> {tpl.clone_count || 0}
                    </div>
                  </div>
                  <p className="font-medium text-sm line-clamp-1">{tpl.name}</p>
                  {tpl.industry && (
                    <Badge variant="outline" className="text-[10px] border-[#2563EB]/30 text-[#2563EB] mt-2">
                      {tpl.industry}
                    </Badge>
                  )}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Content */}
      <Card className="border-[var(--studio-border)] shadow-none">
        <CardHeader>
          <CardTitle className="font-heading text-lg">{t("dashboard.recent_content")}</CardTitle>
        </CardHeader>
        <CardContent>
          {stats.recent_content?.length ? (
            <div className="space-y-3">
              {stats.recent_content.map((item) => {
                const company = companies.find((c) => c.id === item.company_id);
                const st = statusMap[item.status] || statusMap.draft;
                return (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-[var(--studio-bg)]"
                    data-testid={`recent-content-${item.id}`}
                  >
                    <div>
                      <p className="text-sm font-medium">
                        {company?.name || "Unbekannt"} &mdash; {item.format_type || item.type}
                      </p>
                      <p className="text-xs text-[var(--studio-text-muted)]">
                        {item.goal} &middot;{" "}
                        {new Date(item.created_at).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" })}
                      </p>
                    </div>
                    <Badge className={`${st.cls} border-0 text-xs`}>{st.label}</Badge>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-[var(--studio-text-muted)] text-center py-8">
              {t("dashboard.no_content")}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Topic Suggestions */}
      <Card className="border-[var(--studio-border)] shadow-none">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-500" /> {t("dashboard.topic_suggestions")}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={suggestCompany} onValueChange={setSuggestCompany}>
                <SelectTrigger className="w-40 h-8 text-xs" data-testid="suggest-company-select">
                  <SelectValue placeholder={t("dashboard.select_company")} />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" onClick={generateSuggestions} disabled={!suggestCompany || loadingSuggestions}
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-8 text-xs" data-testid="generate-topics-btn">
                {loadingSuggestions ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                {t("dashboard.suggestions_btn")}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {suggestions?.length > 0 ? (
            <div className="space-y-2">
              {suggestions.map((s, i) => (
                <div key={i} className="p-3 rounded-lg bg-[var(--studio-bg)] animate-fade-in" data-testid={`suggestion-${i}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium">{s.title}</p>
                    <Badge variant="outline" className="text-[10px] capitalize">{s.type}</Badge>
                    <Badge className={`text-[10px] border-0 ${
                      s.urgency === "hoch" ? "bg-red-100 text-red-800" :
                      s.urgency === "mittel" ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"
                    }`}>{s.urgency}</Badge>
                  </div>
                  <p className="text-xs text-[var(--studio-text-muted)]">{s.description}</p>
                  {s.reason && <p className="text-xs text-[var(--studio-text-muted)] mt-1 italic">{s.reason}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--studio-text-muted)] text-center py-6">
              {t("dashboard.suggestions_empty")}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
